/**
 * WhatsNewWidget — the TopBar "What's new" affordance (Day 100). A §13 custom
 * popover (sibling shape to the notifications panel / drafts dropdown) listing
 * the latest published release_notes: version + date + optional title + plain
 * highlight bullets. It NEVER imports MarkdownView — highlights are plain strings
 * and arbi-root stays markdown-free (the shared markdown renderer is tree-shaken
 * out of the arbi-root bundle).
 *
 * Unread dot: localStorage `arbi-last-seen-release` (the arbi-theme / arbi-density
 * / arbi-active-distributor precedent, UI_STANDARDS §17). The dot shows when the
 * latest published version differs from the stored last-seen; opening the panel
 * marks it seen. Cross-device staleness is acceptable for V1.
 *
 * "See all" cross-navigates to the arbi-docs Release Notes page via
 * window.location.assign — the TopBar.tsx:266 cross-origin path (React Router
 * can't navigate a different origin). There is no per-release detail body
 * (RBP:1379 = date/version/highlights); the page is the full archive.
 */
import { useState, useRef, useEffect, type JSX } from 'react';
import { Sparkles, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { Database } from '../../lib/database.types';

type ReleaseNote = Database['public']['Tables']['release_notes']['Row'];

const LAST_SEEN_KEY = 'arbi-last-seen-release';
const ARBI_DOCS_RELEASES_URL = 'https://arbi-docs.ironcladsupply.co/release-notes';

function readLastSeen(): string | null {
  try {
    return localStorage.getItem(LAST_SEEN_KEY);
  } catch {
    return null;
  }
}
function writeLastSeen(version: string): void {
  try {
    localStorage.setItem(LAST_SEEN_KEY, version);
  } catch {
    /* ignore — private mode / storage disabled */
  }
}

// highlights is a jsonb column (generated type Json) — coerce to a string list.
function toBullets(h: ReleaseNote['highlights']): string[] {
  return Array.isArray(h) ? h.filter((x): x is string => typeof x === 'string') : [];
}
function fmtDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function WhatsNewWidget(): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  const [releases, setReleases] = useState<ReleaseNote[]>([]);
  const [lastSeen, setLastSeen] = useState<string | null>(() => readLastSeen());
  const panelRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  // Fetch the latest published releases once on mount — the unread dot needs the
  // latest version before the panel opens. Published-only even for admin: the
  // widget shows the user-facing view, not drafts.
  useEffect(() => {
    let active = true;
    void (async () => {
      const { data } = await supabase
        .from('release_notes')
        .select('*')
        .eq('is_published', true)
        .order('released_on', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(5);
      if (active) setReleases(data ?? []);
    })();
    return () => {
      active = false;
    };
  }, []);

  // Close on click-outside / Escape (mirrors the notifications panel).
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent): void {
      const target = e.target as Node | null;
      if (panelRef.current?.contains(target)) return;
      if (buttonRef.current?.contains(target)) return;
      setOpen(false);
    }
    function handleEscape(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const latestVersion = releases[0]?.version ?? null;
  const hasUnread = latestVersion !== null && latestVersion !== lastSeen;

  function toggle(): void {
    setOpen((prev) => {
      const next = !prev;
      if (next && latestVersion) {
        // Mark seen on open.
        writeLastSeen(latestVersion);
        setLastSeen(latestVersion);
      }
      return next;
    });
  }

  function seeAll(): void {
    setOpen(false);
    window.location.assign(ARBI_DOCS_RELEASES_URL);
  }

  return (
    <>
      <button
        ref={buttonRef}
        onClick={toggle}
        className="p-1.5 rounded text-ink-muted hover:text-ink-primary hover:bg-white/10 transition-colors cursor-pointer relative"
        title="What's new"
        aria-label="What's new"
      >
        <Sparkles size={14} />
        {hasUnread && (
          <span
            className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-accent"
            aria-hidden="true"
          />
        )}
      </button>

      {open && (
        <div ref={panelRef}>
          <div
            className="fixed top-[52px] right-0 w-80 h-[calc(100vh-52px-36px)] bg-surface-1 border-l border-border shadow-lg flex flex-col"
            style={{ zIndex: 'var(--z-dropdown, 100)' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
              <span className="text-sm font-semibold text-ink-bright">What&apos;s new</span>
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded text-ink-muted hover:text-ink-primary hover:bg-surface-3 transition-colors cursor-pointer"
                aria-label="Close what's new"
              >
                <X size={14} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto">
              {releases.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center px-4">
                  <Sparkles size={40} className="text-ink-muted mb-3" />
                  <p className="text-sm text-ink-muted">No release notes yet</p>
                </div>
              ) : (
                releases.map((r) => {
                  const bullets = toBullets(r.highlights);
                  return (
                    <div key={r.id} className="px-4 py-3 border-b border-border-subtle">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-mono text-xs font-semibold text-accent">
                          {r.version}
                        </span>
                        {r.title && (
                          <span className="text-xs font-medium text-ink-primary">{r.title}</span>
                        )}
                        <span className="text-2xs text-ink-muted">{fmtDate(r.released_on)}</span>
                      </div>
                      {bullets.length > 0 && (
                        <ul className="mt-1.5 space-y-1 list-disc ml-4">
                          {bullets.map((b, i) => (
                            <li key={i} className="text-2xs text-ink-secondary">
                              {b}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer — see all */}
            <div className="px-4 py-3 border-t border-border flex-shrink-0">
              <button
                onClick={seeAll}
                className="text-xs text-accent hover:text-accent-hover transition-colors cursor-pointer"
              >
                See all release notes →
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
